import type { AttendanceRecordApiRow, PunchEventApiRow } from '@/types/attendance';

export function resolvePunchEventLabel(
  ev: PunchEventApiRow,
  index: number,
  events: PunchEventApiRow[],
): 'Punch In' | 'Punch' | 'Punch Out' {
  if (ev.type === 'in') {
    const isFirstIn = !events.slice(0, index).some((e) => e.type === 'in');
    return isFirstIn ? 'Punch In' : 'Punch';
  }
  if (/mid-day|break/i.test(ev.notes ?? '')) return 'Punch';
  return 'Punch Out';
}

export function getPunchEvents(record: AttendanceRecordApiRow | null | undefined): PunchEventApiRow[] {
  if (record?.punchEvents != null) {
    return record.punchEvents;
  }
  const legacy: PunchEventApiRow[] = [];
  if (record?.checkIn) {
    legacy.push({
      type: 'in',
      at: record.checkIn,
      location: record.checkInLocation ?? undefined,
    });
  }
  if (record?.checkOut) {
    legacy.push({
      type: 'out',
      at: record.checkOut,
      location: record.checkOutLocation ?? undefined,
    });
  }
  return legacy;
}

export function resolvePunchFlags(record: AttendanceRecordApiRow | null | undefined): {
  canPunchIn: boolean;
  canPunchOut: boolean;
  isPunchedIn: boolean;
  openPunchInAt: string | null;
  shiftStart: string | null;
  shiftEnd: string | null;
  dayClosed: boolean;
} {
  if (record?.canPunchIn != null && record?.canPunchOut != null) {
    let dayClosed = Boolean(record.dayClosed);
    const events = record.punchEvents ?? getPunchEvents(record);
    const last = events.length > 0 ? events[events.length - 1] : null;
    if (!dayClosed && last?.type === 'out' && !/mid-day|break/i.test(last.notes ?? '')) {
      dayClosed = true;
    }
    return {
      canPunchIn: dayClosed ? false : record.canPunchIn,
      canPunchOut: dayClosed ? false : record.canPunchOut,
      isPunchedIn: record.isPunchedIn ?? record.canPunchOut,
      openPunchInAt: record.openPunchInAt ?? null,
      shiftStart: record.shiftStart ?? record.checkIn ?? null,
      shiftEnd: record.shiftEnd ?? record.checkOut ?? null,
      dayClosed,
    };
  }

  const events = getPunchEvents(record);
  const last = events.length > 0 ? events[events.length - 1] : null;
  const ins = events.filter((e) => e.type === 'in');
  const outs = events.filter((e) => e.type === 'out');
  let dayClosed = Boolean(record?.dayClosed);
  if (!dayClosed && last?.type === 'out') {
    const breakOut = /mid-day|break/i.test(last.notes ?? '');
    if (!breakOut) dayClosed = true;
  }

  return {
    canPunchIn: dayClosed ? false : !last || last.type === 'out',
    canPunchOut: dayClosed ? false : last?.type === 'in',
    isPunchedIn: last?.type === 'in',
    openPunchInAt: last?.type === 'in' ? last.at : null,
    shiftStart: ins[0]?.at ?? record?.checkIn ?? null,
    shiftEnd: outs.length > 0 ? outs[outs.length - 1]!.at : record?.checkOut ?? null,
    dayClosed,
  };
}

export function computeLiveWorkHours(
  record: AttendanceRecordApiRow | null | undefined,
  asOfMs = Date.now(),
): number | null {
  if (record?.workHours != null && record.workHours > 0) {
    const events = getPunchEvents(record);
    const last = events[events.length - 1];
    if (last?.type === 'out') return record.workHours;
  }

  const events = getPunchEvents(record);
  if (events.length === 0) return record?.workHours ?? null;

  let totalMs = 0;
  let openIn: number | null = null;

  for (const ev of events) {
    const t = new Date(ev.at).getTime();
    if (ev.type === 'in') openIn = t;
    else if (ev.type === 'out' && openIn != null) {
      totalMs += Math.max(0, t - openIn);
      openIn = null;
    }
  }
  if (openIn != null) totalMs += Math.max(0, asOfMs - openIn);

  return Math.round((totalMs / (1000 * 60 * 60)) * 100) / 100;
}
